/**
 * <cart-upsell>
 * AI-driven cart drawer upsell for Dawn / Dawn-based Shopify themes.
 *
 * - Uses /recommendations/products.json?intent=related, seeded on the
 *   most recently added cart item.
 * - Filters out products already in the cart.
 * - Quick Add for single-variant products; "View product" link otherwise.
 * - Hooks into Dawn's cart-drawer renderContents() + PUB_SUB cartUpdate.
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.inner = this.querySelector('.cart-upsell__inner');
    this.scroller = this.querySelector('.cart-upsell__scroller');
    this.template = this.querySelector('.cart-upsell__card-template');

    this.maxCards = parseInt(this.dataset.maxCards, 10) || 3;
    this.candidateLimit = parseInt(this.dataset.candidateLimit, 10) || 10;

    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.cartItemIds = new Set();
    this.lastSeedId = null;
    this._refreshing = false;
  }

  connectedCallback() {
    this.refresh();

    // Subscribe to Dawn's cart-update pub/sub if available.
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCart = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Avoid loop: if we triggered this update ourselves we already refreshed.
        if (event && event.source === 'cart-upsell') return;
        this.refresh();
      });
    }

    // Fallback event for non-Dawn or customized themes.
    this._onCartRefresh = () => this.refresh();
    document.addEventListener('cart:refresh', this._onCartRefresh);
  }

  disconnectedCallback() {
    if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
    document.removeEventListener('cart:refresh', this._onCartRefresh);
  }

  async refresh() {
    if (this._refreshing) return;
    this._refreshing = true;

    try {
      const cart = await fetch(`${this.routeRoot}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());

      if (!cart.items || cart.items.length === 0) {
        this.hide();
        this.lastSeedId = null;
        return;
      }

      this.cartItemIds = new Set(cart.items.map((item) => item.product_id));

      // Seed off the most recently added line item — strongest signal.
      const seedItem = cart.items[cart.items.length - 1];
      const seedId = seedItem.product_id;
      this.lastSeedId = seedId;

      const recsUrl = `${this.routeRoot}recommendations/products.json?product_id=${seedId}&limit=${this.candidateLimit}&intent=related`;
      const sectionUrl = `${this.routeRoot}recommendations/products?product_id=${seedId}&limit=${this.candidateLimit}&intent=related&section_id=upsell-data`;

      const [res, sectionRes] = await Promise.all([
        fetch(recsUrl, { credentials: 'same-origin' }),
        fetch(sectionUrl, { credentials: 'same-origin' }).catch(() => null),
      ]);

      if (!res.ok) {
        this.hide();
        return;
      }
      const data = await res.json();

      this.shortTitleMap = {};
      if (sectionRes && sectionRes.ok) {
        try {
          const html = await sectionRes.text();
          const match = html.match(/<script[^>]*data-upsell-meta[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            JSON.parse(match[1]).forEach(({ handle, short_title }) => {
              if (handle && short_title) this.shortTitleMap[handle] = short_title;
            });
          }
        } catch (e) { /* fall back to product.title */ }
      }

      const filtered = (data.products || [])
        .filter((p) => !this.cartItemIds.has(p.id))
        .slice(0, this.maxCards);

      if (filtered.length === 0) {
        this.hide();
        return;
      }

      this.render(filtered);
      this.show();
    } catch (err) {
      console.warn('[cart-upsell] refresh failed:', err);
      this.hide();
    } finally {
      this._refreshing = false;
    }
  }

  show() {
    this.inner.hidden = false;
  }

  hide() {
    this.inner.hidden = true;
  }

  render(products) {
    this.scroller.innerHTML = '';

    products.forEach((product) => {
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.productId = product.id;

      const productUrl = product.url || `${this.routeRoot}products/${product.handle}`;
      const mediaLink = card.querySelector('.cart-upsell__media');
      const img = card.querySelector('.cart-upsell__image');
      const titleLink = card.querySelector('.cart-upsell__title');
      const priceEl = card.querySelector('.cart-upsell__price');
      const addBtn = card.querySelector('.cart-upsell__add');
      const viewLink = card.querySelector('.cart-upsell__view');

      // Links
      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-hidden', 'false');
      mediaLink.removeAttribute('tabindex');
      mediaLink.setAttribute('aria-label', product.title);
      titleLink.href = productUrl;
      titleLink.textContent = (this.shortTitleMap && this.shortTitleMap[product.handle]) || product.title;
      viewLink.href = productUrl;

      // Image
      const imgSrc = this._extractImageSrc(product);
      if (imgSrc) {
        img.src = this._sizedImage(imgSrc, '400x400');
        img.alt = product.featured_image && product.featured_image.alt ? product.featured_image.alt : product.title;
      } else {
        img.remove();
      }

      // Price
      priceEl.textContent = this._formatMoney(product.price);

      // Variant logic — Quick Add only when product has exactly one variant.
      const variants = product.variants || [];
      const availableVariants = variants.filter((v) => v.available);
      const isSingleVariant = variants.length === 1;

      if (availableVariants.length === 0) {
        // Sold out
        addBtn.disabled = true;
        addBtn.classList.add('cart-upsell__add--disabled');
        addBtn.querySelector('.cart-upsell__add-text').textContent = 'Sold out';
        viewLink.hidden = true;
      } else if (isSingleVariant) {
        // Quick Add
        const variantId = availableVariants[0].id;
        addBtn.dataset.variantId = variantId;
        addBtn.addEventListener('click', () => this.quickAdd(variantId, addBtn));
        viewLink.hidden = true;
      } else {
        // Multiple variants — direct to PDP
        addBtn.hidden = true;
        viewLink.hidden = false;
      }

      this.scroller.appendChild(card);
    });
  }

  async quickAdd(variantId, button) {
    const textSpan = button.querySelector('.cart-upsell__add-text');
    const loadingSpan = button.querySelector('.cart-upsell__add-loading');

    button.disabled = true;
    if (textSpan) textSpan.hidden = true;
    if (loadingSpan) loadingSpan.hidden = false;

    try {
      const sections = this._sectionsToRender();
      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sections.map((s) => s.section).join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch(`${this.routeRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });
      const data = await res.json();

      if (data.status) {
        // 422 / error response
        console.warn('[cart-upsell] add error:', data.description || data.message);
        return;
      }

      this._updateCartDrawer(data);
      this._publishCartUpdate(variantId, data);

      // Ensure drawer is open
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      // The newly-added row is detected and animated by the global
      // cart-item-added subscriber below (handles upsell, product-page, and
      // any other path that publishes PUB_SUB_EVENTS.cartUpdate).
    } catch (err) {
      console.warn('[cart-upsell] quick add failed:', err);
    } finally {
      // Note: the cart drawer re-render usually destroys this card; this
      // restoration only matters if the render path didn't fire.
      button.disabled = false;
      if (textSpan) textSpan.hidden = false;
      if (loadingSpan) loadingSpan.hidden = true;
    }
  }

  // --- helpers -------------------------------------------------------------

  _updateCartDrawer(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(parsedState);
      return;
    }

    // Manual fallback: replace section HTML using returned sections.
    this._sectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.section];
      if (!html) return;
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector(section.selector || '.shopify-section');
      if (replacement) target.innerHTML = replacement.innerHTML;
    });
  }

  _publishCartUpdate(variantId, cartData) {
    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-upsell',
        productVariantId: String(variantId),
        cartData,
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartData }));
    }
  }

  _sectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  _extractImageSrc(product) {
    if (!product) return null;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && (product.featured_image.url || product.featured_image.src)) {
      return product.featured_image.url || product.featured_image.src;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first.url;
    }
    return null;
  }

  _sizedImage(src, size) {
    if (!src) return '';
    // Normalize protocol-relative URLs
    let url = src.startsWith('//') ? `https:${src}` : src;
    // Inject _SIZE before file extension
    url = url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, `_${size}$1$2`);
    return url;
  }

  _formatMoney(price) {
    const cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (!Number.isFinite(cents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {
        /* fall through */
      }
    }

    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

/**
 * Global cart-item add animation.
 *
 * Detects whenever a new variant appears in the cart-drawer (regardless of
 * which code path added it — upsell, product page, etc.), hides the row
 * synchronously before the browser paints, waits for its image to finish
 * loading, then animates the row in. Prevents the bitmap-pops-after-text
 * flash on every cart add.
 */
(function setupCartItemAddAnimation() {
  const snapshot = () => {
    const set = new Set();
    document
      .querySelectorAll('.cart-drawer .cart-item[data-variant-id]')
      .forEach((el) => set.add(el.dataset.variantId));
    return set;
  };

  let knownVariantIds = snapshot();

  const isDrawerOpen = () => {
    const drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  };

  const animateRow = (row) => {
    row.setAttribute('data-pending-animation', '');

    let imageReady = false;
    let drawerReady = isDrawerOpen();

    const tryReveal = () => {
      if (!imageReady || !drawerReady) return;
      row.removeAttribute('data-pending-animation');
      row.classList.add('cart-item--just-added');
      setTimeout(() => row.classList.remove('cart-item--just-added'), 1000);
    };

    const img = row.querySelector('img');
    if (!img || img.complete) {
      imageReady = true;
    } else {
      let done = false;
      const onImg = () => {
        if (done) return;
        done = true;
        imageReady = true;
        tryReveal();
      };
      img.addEventListener('load', onImg, { once: true });
      img.addEventListener('error', onImg, { once: true });
      // Safety: never block the animation indefinitely.
      setTimeout(onImg, 1500);
    }

    if (!drawerReady) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const observer = new MutationObserver(() => {
          if (isDrawerOpen()) {
            drawerReady = true;
            observer.disconnect();
            tryReveal();
          }
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        // Safety: if the drawer never opens (e.g. user navigates away),
        // release after 3s so we don't leak observers.
        setTimeout(() => {
          drawerReady = true;
          observer.disconnect();
          tryReveal();
        }, 3000);
      }
    }

    tryReveal();
  };

  const onCartUpdate = () => {
    const current = snapshot();
    const newVariants = [];
    current.forEach((id) => {
      if (!knownVariantIds.has(id)) newVariants.push(id);
    });
    knownVariantIds = current;

    newVariants.forEach((variantId) => {
      const row = document.querySelector(
        `.cart-drawer .cart-item[data-variant-id="${variantId}"]`
      );
      if (row) animateRow(row);
    });
  };

  if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }
})();

/**
 * "Clear cart" button.
 *
 * Bound via event delegation on document so it survives the drawer's HTML
 * being replaced on every cart update. Uses /cart/update.js with all line
 * quantities set to 0 (one request, returns rendered sections for an
 * atomic drawer refresh).
 */
(function setupClearCart() {
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-clear-cart]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    btn.disabled = true;

    try {
      const res = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('clear failed');

      // Verify the cart is actually empty before reloading. A second
      // /cart.js read forces the browser to settle the cart token cookie
      // — without this, the reload sometimes ships the OLD cart cookie
      // and the server re-renders with items still present.
      try {
        const verify = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await verify.json();
        if (cart && cart.item_count > 0) {
          console.warn('[cart-clear] cart not empty after /cart/clear.js', cart);
        }
      } catch (_) { /* non-fatal — still reload */ }

      // Cache-bust the reload so the browser doesn't serve a stale render
      // of the cart-with-items state.
      const url = new URL(window.location.href);
      url.searchParams.set('_cc', Date.now().toString());
      window.location.replace(url.toString());
    } catch (err) {
      console.warn('[cart-clear] failed:', err);
      btn.disabled = false;
    }
  });
})();
